import { ref, nextTick } from 'vue';

/**
 * Calculates and maintains the position and width of the dropdown popup.
 *
 * When drop-position="fixed" is used inside an element with a CSS transform
 * (e.g. a modal container), pass parentSelector so that the coordinates are
 * adjusted relative to the transformed ancestor. This is needed because CSS
 * transform creates a new containing block for position:fixed descendants,
 * meaning their top/left are relative to the transformed parent, not the
 * viewport.
 */
export function useDropdownPosition() {
  const popupStyles = ref({});
  const dropWidth = ref(0);

  function calcPopupPosition(triggerEl, dropEl, { mode, fixedWidth = 0, parentSelector = '' }) {
    if (!triggerEl || !dropEl) return;

    if (mode === 'fixed') {
      _calcFixed(triggerEl, dropEl, fixedWidth, parentSelector);
    } else {
      _calcAbsolute(triggerEl, dropEl, fixedWidth);
    }
  }

  function _calcAbsolute(triggerEl, dropEl, fixedWidth) {
    const coords = triggerEl.getBoundingClientRect();
    const componentWidth = coords.width;
    const dropW = fixedWidth || componentWidth;
    const clientHeight = document.body.clientHeight;
    const dropHeight = dropEl.offsetHeight;

    const canShowBottom = coords.bottom + dropHeight < clientHeight - 30;
    const canShowTop = coords.top - dropHeight > 10;
    const dropToBottom = (!canShowBottom && !canShowTop) || canShowBottom;

    popupStyles.value = {
      position: 'absolute',
      width: `${dropW}px`,
      top: dropToBottom ? `${coords.height + 5}px` : `-${dropHeight + 5}px`,
    };

    _syncWidth(dropEl, componentWidth, fixedWidth);
  }

  function _calcFixed(triggerEl, dropEl, fixedWidth, parentSelector) {
    const trigger = triggerEl.getBoundingClientRect();
    const clientHeight = document.documentElement.clientHeight;
    const clientWidth = document.documentElement.clientWidth;
    const dropW = fixedWidth || trigger.width;
    const GAP = 5;

    // Vertical: open below trigger; open above only if there is not enough space below
    // and there IS enough space above. offsetHeight may not be fully settled on the
    // first render tick, so we use the CSS max-height as a fallback.
    const dropHeight = dropEl.offsetHeight > 0
      ? dropEl.offsetHeight
      : (parseInt(dropEl.style.maxHeight) || 240);
    const spaceBelow = clientHeight - trigger.bottom;
    const spaceAbove = trigger.top;
    const openBelow = spaceBelow >= dropHeight || spaceBelow >= spaceAbove;

    // Viewport-relative coordinates
    const viewportTop = openBelow ? trigger.bottom + GAP : null;
    const viewportBottomAnchor = !openBelow ? trigger.top - GAP : null; // top of trigger minus gap
    let viewportLeft = (trigger.left + dropW <= clientWidth)
      ? trigger.left
      : Math.max(0, trigger.right - dropW);

    // When the drop is inside a transformed ancestor (e.g. a modal with
    // transform: translateY(0)), position:fixed is relative to that ancestor,
    // not the viewport. Subtract the ancestor's bounding rect to compensate.
    const parentEl = parentSelector ? document.querySelector(parentSelector) : null;
    const parentRect = parentEl ? parentEl.getBoundingClientRect() : null;

    let top = 'auto';
    let bottom = 'auto';
    let left;

    if (parentRect) {
      if (viewportTop !== null) {
        top = `${viewportTop - parentRect.top}px`;
      } else {
        bottom = `${parentRect.bottom - viewportBottomAnchor}px`;
      }
      left = `${viewportLeft - parentRect.left}px`;
    } else {
      if (viewportTop !== null) {
        top = `${viewportTop}px`;
      } else {
        bottom = `${clientHeight - viewportBottomAnchor}px`;
      }
      left = `${viewportLeft}px`;
    }

    popupStyles.value = {
      position: 'fixed',
      width: `${dropW}px`,
      top,
      bottom,
      left,
    };

    _syncWidth(dropEl, trigger.width, fixedWidth);
  }

  function _syncWidth(dropEl, componentWidth, fixedWidth) {
    nextTick(() => {
      const scrollWidth = dropEl.scrollWidth;

      dropWidth.value = fixedWidth
        ? Math.max(scrollWidth, fixedWidth)
        : Math.max(scrollWidth, componentWidth);
    });
  }

  return { popupStyles, dropWidth, calcPopupPosition };
}
